/**
 * 日期与文案的纯函数。**界面层唯一值得单测的地方** —— 它们没有 React、
 * 没有网络，输入输出都是可断言的。
 *
 * ⚠️ 两个刻意的裁定：
 * - 时区一律按**浏览器本地**（`new Date(iso)` 后取 `getFullYear()` 等），
 *   与「用户看到的是自己那天写的」一致。ISO 字符串本身是 UTC。
 * - 倒计时与「已配对 N 天」在**客户端**算（spec §6.2），后端只给时间戳。
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** `HH:mm`，本地时区。 */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `YYYY-MM-DD HH:mm`，本地时区。设置页与详情页用。 */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** 本地日历日的 key，用于分组。 */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * 分组头文案：`今天` / `昨天` / `M月D日`。
 *
 * 只到「日」，不到「周几」—— 日记的时间感是「那天」，不是「星期几」。
 */
export function formatDayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const diffDays = Math.round(
    (startOfLocalDay(now) - startOfLocalDay(d)) / DAY_MS,
  );
  if (diffDays === 0) return "今天";
  if (diffDays === 1) return "昨天";
  // 同年省略年份，跨年补上——否则去年 10 月 3 日与今年读起来一样。
  const md = `${d.getMonth() + 1}月${d.getDate()}日`;
  return d.getFullYear() === now.getFullYear()
    ? md
    : `${d.getFullYear()}年${md}`;
}

export interface DayGroup<T> {
  key: string;
  label: string;
  items: T[];
}

/**
 * 按本地日期分组，**保持输入顺序**（后端按 `writtenAt` 倒序返回）。
 *
 * 排序交给后端 —— 前端再排一次会产生两个排序来源，早晚分岔。
 */
export function groupByDay<T extends { writtenAt: string }>(
  entries: T[],
  now: Date = new Date(),
): DayGroup<T>[] {
  const groups: DayGroup<T>[] = [];
  let current: DayGroup<T> | null = null;
  for (const entry of entries) {
    const key = localDayKey(entry.writtenAt);
    if (current === null || current.key !== key) {
      current = { key, label: formatDayLabel(entry.writtenAt, now), items: [] };
      groups.push(current);
    }
    current.items.push(entry);
  }
  return groups;
}

/**
 * 「已配对 N 天」。
 *
 * 配对当天记作第 1 天（`+1`）—— 「今天刚配对」显示「已配对 1 天」比「0 天」
 * 更符合中文语感。未配对返回 `null`。
 */
export function daysTogether(
  pairedAt: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (pairedAt == null) return null;
  const d = new Date(pairedAt);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((startOfLocalDay(now) - startOfLocalDay(d)) / DAY_MS) + 1;
}

export interface Countdown {
  /** 距下一次还有几天；今天为 0。 */
  days: number;
  /** 下一次发生的日期（本地）。 */
  next: Date;
  /** 今天就是。 */
  isToday: boolean;
}

/**
 * 纪念日倒计时 —— 复现「每年一次」。
 *
 * `date` 是 `YYYY-MM-DD`（带年份）。下一次 = 今年的月日，若已过则明年。
 * 用构造 `Date(y, m, d)` 的本地日历，不使用 `Date.parse`（那会按 UTC）。
 */
export function anniversaryCountdown(
  date: string,
  now: Date = new Date(),
): Countdown {
  const [y, m, d] = date.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    return { days: 0, next: now, isToday: false };
  }
  const thisYear = new Date(now.getFullYear(), m - 1, d);
  const next =
    startOfLocalDay(thisYear) < startOfLocalDay(now)
      ? new Date(now.getFullYear() + 1, m - 1, d)
      : thisYear;
  const days = Math.round(
    (startOfLocalDay(next) - startOfLocalDay(now)) / DAY_MS,
  );
  return { days, next, isToday: days === 0 };
}

/** 倒计时文案：`今天` / `还有 N 天`。 */
export function countdownLabel(c: Countdown): string {
  if (c.isToday) return "今天";
  return `还有 ${c.days} 天`;
}

/** 纪念日上的月/日徽标数字。 */
export function monthDayBadge(date: string): { m: number; d: number } {
  const [, m, d] = date.split("-").map(Number);
  return { m: m ?? 1, d: d ?? 1 };
}

/** `2026年10月24日`。 */
export function formatAnniversaryDate(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return date;
  return `${y}年${m}月${d}日`;
}

/** 是否为同一天（本地）。用于「TA 今天分享了」的判断。 */
export function isSameLocalDay(iso: string, now: Date = new Date()): boolean {
  const d = new Date(iso);
  return startOfLocalDay(d) === startOfLocalDay(now);
}

/** 人可读的文件大小。 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** 正文摘要：折叠换行与空白，截到 `n` 字。列表卡片用。 */
export function excerpt(text: string, n = 120): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > n ? `${flat.slice(0, n)}…` : flat;
}
